<template>
  <div>
    <b-row class="card-body">
      <b-col cols="12" md="8">
        <label>{{ 'advance_invoice' | translate('global') }}</label>
        <b-form-group>
          <treeselect value-format="object" :default-expand-level="1"
                      :placeholder="'advance_invoice_select' | translate('global')"
                      instanceId="loadAdvanceInvoices" :load-options="loadOptions" :async="true" :cacheOptions="false"
                      :searchPromptText="'invoice_autocomplete' | translate('global')"
                      :noResultsText="'no_results_found' | translate('global')"
                      v-model="selectedAdvanceInvoice"/>
        </b-form-group>
      </b-col>
      <b-col cols="12" md="4">
        <div class="flex-container">
          <b-button variant="success" :disabled="!canAssign" class="mt-md-29 mr-2" @click="onAssign()">
            <b-spinner v-if="spinners.isApproving" type="grow" small/>
            {{ 'advance_invoices_assign_to_order' | translate('global') }}
          </b-button>
          <b-button variant="primary"
                    class="mt-md-29 ml-auto"
                    target="_blank"
                    :to="{ name: 'AdvanceInvoiceCreate', query: { supplier: this.selectedSupplier, structure: this.selectedStructure } }">
            <i class="fa fa-plus mr-2"/>{{ 'advance_invoices_new_title' | translate('global') }}
          </b-button>
        </div>
      </b-col>
      <b-col cols="12">
        <div v-if="contentIsLoading" class="text-center mt-5 mb-5">
          <b-spinner type="grow" variant="primary"/>
        </div>
        <div v-else class="mt-3">
          <EmptyTable v-if="!advanceInvoicesTableData.length"/>
          <div v-else>
            <AdvanceInvoiceTable :data="advanceInvoicesTableData"/>
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>
<script>
  import { mapGetters, mapActions } from 'vuex';
  import statuses from '@/store/statuses';
  import VuexTypes from '@/store/types/index';
  import Services from '@/services/index';
  import TreeselectMixin from '@/mixins/TreeselectMixin';
  import Treeselect from '@riophae/vue-treeselect';
  import forEach from 'lodash.foreach';
  import EmptyTable from '@/views/sharedComponents/EmptyTableData';
  import AdvanceInvoiceTable from './AdvanceInvoiceTable';

  const OrdersTypes = VuexTypes.orders;
  const OrdersService = Services.orders;

  export default {
    mounted() {
      this.getData();
    },
    mixins: [TreeselectMixin],
    data() {
      return {
        spinners: {
          isApproving: false,
          isRemoving: false
        },
        advanceInvoicesTableData: [],
        selectedAdvanceInvoice: null,
        canAssignAdvanceInvoice: false,
        selectedSupplier: null,
        selectedStructure: null
      };
    },
    components: { AdvanceInvoiceTable, Treeselect, EmptyTable },
    computed: {
      ...mapGetters('Orders', [
        'orderUpdateData',
        'orderInvoicesAdvanceList',
        'orderApproveInvoiceAdvance',
        'orderRemoveInvoiceAdvance',
        'orderSendInvoiceAdvanceToBookkeeper',
        'orderApproveInvoiceAdvanceReview',
        'orderDeclineInvoiceAdvanceReview',
        'orderSendInvoiceAdvanceToBookkeeper'
      ]),
      contentIsLoading() {
        if (this.orderInvoicesAdvanceList.status === statuses.LOADED) {
          return false;
        }
        return true;
      },
      canAssign() {
        return this.selectedAdvanceInvoice && this.canAssignAdvanceInvoice;
      }
    },
    watch: {
      orderUpdateData: {
        handler: function () {
          if (this.orderUpdateData.status === statuses.LOADED) {
            this.selectedSupplier = this.orderUpdateData.data.order.supplier.id;
            this.selectedStructure = this.orderUpdateData.data.order.structure.id;
          }
        },
        deep: true,
        immediate: true
      },
      orderInvoicesAdvanceList: {
        handler: function () {
          if (this.orderInvoicesAdvanceList.status === statuses.LOADED) {
            this.fillData();
          }
        },
        deep: true
      },
      orderApproveInvoiceAdvance: {
        handler: function () {
          if (this.orderApproveInvoiceAdvance.status === statuses.LOADED) {
            this.getData();
            this.alertSuccess();
          } else if (this.orderApproveInvoiceAdvance.status === statuses.ERROR) {
            this.alertError();
            this.errorMsg = this.orderApproveInvoiceAdvance.data.data.errors;
          }
          this.spinners.isApproving = false;
        },
        deep: true
      },
      orderRemoveInvoiceAdvance: {
        handler: function () {
          if (this.orderRemoveInvoiceAdvance.status === statuses.LOADED) {
            this.getData();
            this.alertSuccess();
          } else if (this.orderRemoveInvoiceAdvance.status === statuses.ERROR) {
            this.alertError();
            this.errorMsg = this.orderRemoveInvoiceAdvance.data.data.errors;
          }
          this.spinners.isRemoving = false;
        },
        deep: true
      },
      orderApproveInvoiceAdvanceReview: {
        handler: function () {
          if (this.orderApproveInvoiceAdvanceReview.status === statuses.LOADED) {
            this.getData();
          }
          this.spinners.isRemoving = false;
        },
        deep: true
      },
      orderDeclineInvoiceAdvanceReview: {
        handler: function () {
          if (this.orderDeclineInvoiceAdvanceReview.status === statuses.LOADED) {
            this.getData();
          }
          this.spinners.isRemoving = false;
        },
        deep: true
      },
      orderSendInvoiceAdvanceToBookkeeper: {
        handler: function () {
          if (this.orderSendInvoiceAdvanceToBookkeeper.status === statuses.LOADED) {
            this.getData();
          }
          this.spinners.isRemoving = false;
        },
        deep: true
      }
    },
    methods: {
      ...mapActions('Orders', [
        OrdersTypes.ORDER_INVOICES_ADVANCE_LIST,
        OrdersTypes.ORDER_APPROVE_INVOICE_ADVANCE
      ]),
      getData() {
        this.ORDER_INVOICES_ADVANCE_LIST(this.$route.params.id);
      },
      fillData() {
        this.advanceInvoicesTableData = this.orderInvoicesAdvanceList.data.advanceInvoices;
        this.canAssignAdvanceInvoice = this.orderInvoicesAdvanceList.data.actions.assignAdvanceInvoice;
      },
      onAssign() {
        this.spinners.isApproving = true;
        this.ORDER_APPROVE_INVOICE_ADVANCE({
          orderId: this.$route.params.id,
          advanceInvoiceId: this.selectedAdvanceInvoice.id
        });
      },
      async loadOptions({ action, searchQuery, callback, instanceId }) {
        let options = [];
        const query = searchQuery.trim();
        if (action === 'ASYNC_SEARCH') {
          const valid = this.checkSearchQuery(query);
          if (valid) {
            options = await this[instanceId](query);
          }
        }
        callback(null, options);
      },
      async loadAdvanceInvoices(name) {
        const param = name || '';
        const data = await OrdersService.searchInvoiceAdvance(this.$route.params.id, param);
        const options = [];
        forEach(data.data.data, (item) => {
          options.push({
            id: item['id'],
            label: `${item['number']}, (
          ${this.$t('sum', 'global')} ${item['sum']},
          ${this.$t('issue_at', 'global')} ${item['issue_at']}
          )`
          });
        });
        return options;
      },
    }
  };
</script>

<style scoped>
</style>
